import {ffLazyLoading, hasFeatureFlag, Time} from 'yougile-util'
import {Chat, ChatMessageJSON, extensionPublicHubTitlePrefix, Hub} from './'
import {ChatJSON} from './chat'
import {type DataObject, DataObjectId, DataObjectJSON} from './data-object'
import {HubJSON, HubProperties} from './hubs'

export class HubUtil {
  /**
   * see also `mail-sync` repo, spam messages are marked this way
   */
  static detectSpam(hub: HubJSON): void {
    if (hub?.title?.includes('Spam]')) {
      hub.properties ??= {}
      hub.properties[HubProperties.fromSpam] = true
    }
  }

  static isFromSpam(hub: HubJSON): boolean {
    return !!hub?.properties?.[HubProperties.fromSpam]
  }

  static isPhantom(hub: DataObject | undefined): boolean {
    return !!hub?.properties?.phantom
  }

  static isCreatedByDefault(hub: HubJSON): boolean {
    return Boolean(hub.data?.isCreatedByDefault)
  }

  static makeEmptyChatJSON(): ChatJSON {
    return {
      dataType: 'Chat',
      id: '' as DataObjectId,
      lastSeenMessages: {},
      messages: {},
      timestamp: 0
    }
  }

  private static smallMessage(message: ChatMessageJSON): ChatMessageJSON
  private static smallMessage(
    message: ChatMessageJSON | undefined
  ): ChatMessageJSON | undefined {
    if (!message) {
      return undefined
    }
    const result: ChatMessageJSON = {
      dataType: 'ChatMessage',
      id: '' as DataObjectId,
      from: message.from,
      text: '_',
      timestamp: message.timestamp
    }

    if (message.properties?.fromSystem) {
      result.properties = message.properties
    }

    return result
  }

  private static smallStickers(
    stickers: HubJSON['stickers'] | undefined
  ): HubJSON['stickers'] | undefined
  private static smallStickers(
    stickers: HubJSON['stickers']
  ): HubJSON['stickers'] {
    if (!stickers) {
      return stickers
    }
    const res: HubJSON['stickers'] = {}
    for (const stickerId of Object.keys(stickers)) {
      const sticker = stickers[stickerId]
      if (sticker?.history) {
        res[stickerId] = {...sticker}
        res[stickerId].history = []
      } else {
        res[stickerId] = sticker
      }
    }
    return res
  }

  static makeLazyMessagePlaceholder(
    messages: {[key: number]: ChatMessageJSON},
    forMessage: number,
    type: 'before' | 'after' | 'both'
  ): ChatMessageJSON[] {
    const placeholders: ChatMessageJSON[] = []

    const keys = Object.keys(messages)
    const messageIndex = keys.indexOf(`${forMessage}`)

    if (messageIndex === -1) {
      return placeholders
    }

    const needBefore = type === 'before' || type === 'both'
    const needAfter = type === 'after' || type === 'both'

    if (needBefore && keys[messageIndex - 1]) {
      placeholders.push({
        from: 'lazy' as DataObjectId,
        text: '...',
        dataType: 'ChatMessage',
        id: '' as DataObjectId,
        timestamp: forMessage - 1,
        properties: {
          isLazyPlaceholder: true,
          forMessage: forMessage,
          nextMessage: +keys[messageIndex - 1],
          type: 'before'
        }
      })
    }

    if (needAfter && keys[messageIndex + 1]) {
      placeholders.push({
        from: 'lazy' as DataObjectId,
        text: '...',
        dataType: 'ChatMessage',
        id: '' as DataObjectId,
        timestamp: forMessage + 1,
        properties: {
          isLazyPlaceholder: true,
          forMessage: forMessage,
          nextMessage: +keys[messageIndex + 1],
          type: 'after'
        }
      })
    }

    return placeholders
  }

  static withoutLazyPlaceholders<T extends DataObjectJSON>(entity: T): T {
    if (!entity) {
      return entity
    }
    const isLazyMode = hasFeatureFlag(ffLazyLoading)
    if (!isLazyMode) {
      return entity
    }
    const hub = entity as unknown as HubJSON
    if (hub.chat) {
      return {
        ...hub,
        chat: hub.chat
          ? {
              ...hub.chat,
              messagesCount: undefined,
              messages: Object.fromEntries(
                Object.entries(hub.chat.messages).filter(
                  ([, message]) => !message.properties?.isLazyPlaceholder
                )
              )
            }
          : undefined
      } as unknown as T
    }

    return entity
  }

  static trimChat<HubLikeJSON extends {chat?: ChatJSON}>(
    hub: HubLikeJSON
  ): HubLikeJSON {
    const chat = hub.chat
    if (!chat) {
      return hub
    }

    const maxMessages = 60
    const messages = chat.messages ?? {}
    const allMessageKeys = Object.keys(messages)

    const placeholdersKeys = allMessageKeys.filter(
      key => !!messages[+key]?.properties?.isLazyPlaceholder
    )
    const messageKeys = allMessageKeys.filter(
      key => !messages[+key]?.properties?.isLazyPlaceholder
    )
    if (messageKeys.length <= maxMessages) {
      return hub
    }

    const newMessages: typeof messages = {}
    const newMessageKeys = messageKeys.slice(-maxMessages)
    const minKey = +newMessageKeys[0]

    for (const key of placeholdersKeys) {
      newMessages[+key] = messages[+key]
    }

    const placeholders = HubUtil.makeLazyMessagePlaceholder(
      messages,
      minKey,
      'before'
    )
    for (const placeholder of placeholders) {
      newMessages[placeholder.timestamp] = placeholder
    }

    for (const key of newMessageKeys) {
      newMessages[+key] = messages[+key]
    }

    const pinnedMessages = messageKeys.filter(
      key => !!messages[+key]?.label && !newMessages[+key]
    )

    for (const key of pinnedMessages) {
      newMessages[+key] = messages[+key]
      const placeholders = HubUtil.makeLazyMessagePlaceholder(
        messages,
        +key,
        'both'
      )
      for (const placeholder of placeholders) {
        newMessages[placeholder.timestamp] = placeholder
      }
    }

    // merge placeholders
    const keys = Object.keys(newMessages)
    for (let index = 0; index < keys.length; index++) {
      const key = +keys[index]
      const nextKey = +keys[index + 1]
      const current = newMessages[key]
      const next = newMessages[nextKey]
      if (
        current?.properties?.isLazyPlaceholder &&
        next?.properties?.isLazyPlaceholder &&
        current?.properties.type === next?.properties.type
      ) {
        if (current.properties.type === 'before') {
          current.properties.nextMessage = Math.max(
            +current.properties.nextMessage,
            +next.properties.nextMessage
          )
          current.properties.forMessage = Math.min(
            +current.properties.forMessage,
            +next.properties.forMessage
          )
        } else {
          current.properties.nextMessage = Math.min(
            +current.properties.nextMessage,
            +next.properties.nextMessage
          )
          current.properties.forMessage = Math.max(
            +current.properties.forMessage,
            +next.properties.forMessage
          )
        }
        delete newMessages[nextKey]
      }
    }

    return {
      ...hub,
      chat: {
        ...chat,
        messages: newMessages,
        messagesCount: chat.messagesCount ?? messageKeys.length
      }
    }
  }

  static makeLazyTaskJSON(task: HubJSON, forUser?: DataObjectId): HubJSON {
    const taskChat = task.chat ?? HubUtil.makeEmptyChatJSON()
    const lastMsgTime = Chat.lastMessageForJson(taskChat)
    const lastNotSystemMsgTime = Chat.lastNotSystemMessageForJson(taskChat)

    let lastSeenMessages = taskChat.lastSeenMessages
    if (forUser && taskChat.lastSeenMessages?.[forUser]) {
      lastSeenMessages = {
        [forUser]: taskChat.lastSeenMessages[forUser]
      }
    }
    if (forUser && !taskChat.lastSeenMessages?.[forUser]) {
      lastSeenMessages = {}
    }

    const result = {
      ...HubUtil.makeEmptyJSON(task),
      title: '__',
      chat: lastMsgTime
        ? {
            ...taskChat,
            id: '' as DataObjectId,
            lastSeenMessages: lastSeenMessages,
            messages: {} as typeof taskChat.messages
          }
        : undefined,
      isLazy: true,
      data: task.data?.gtd?.state?.stage
        ? {
            gtd: {
              state: {
                stage: task.data?.gtd.state.stage
              }
            }
          }
        : undefined,
      stickers: this.smallStickers(task.stickers),
      complete: task.complete,
      deleted: task.deleted
    }
    const messages = result.chat?.messages
    if (lastMsgTime && messages) {
      messages[lastMsgTime] = this.smallMessage(taskChat.messages[lastMsgTime])
    }
    if (
      messages &&
      lastNotSystemMsgTime &&
      lastNotSystemMsgTime !== lastMsgTime
    ) {
      messages[lastNotSystemMsgTime] = this.smallMessage(
        taskChat.messages[lastNotSystemMsgTime]
      )
    }
    return result
  }

  static makeEmptyJSON(fromJSON: HubJSON): HubJSON
  static makeEmptyJSON(fromJSON: HubJSON | undefined): HubJSON | undefined {
    if (!fromJSON) {
      return fromJSON
    }
    return {
      id: fromJSON.id,
      dataType: fromJSON.dataType,
      location: fromJSON.location,
      revision: fromJSON.revision,
      timestamp: fromJSON.timestamp,

      chat: HubUtil.makeEmptyChatJSON(),
      stickers: undefined,
      complete: undefined,
      connectedHubs: undefined,
      data: undefined,
      deleted: true,
      title: fromJSON.id,
      locHistory: undefined,

      // save original deleted
      properties: {del: fromJSON.deleted ? 'yes' : 'no'},
      apiData: undefined,
      description: undefined
    }
  }

  /**
   * Cleanups old extension events with timestamp, to avoid memory
   * leaks, and to avoid the extension events data to grow indefinitely.
   */
  static cleanupExpiredExtensionEvents(hub: Hub | HubJSON): void {
    if (!hub.data?.extensionEvents) {
      return
    }

    const events = Object.keys(hub.data.extensionEvents)

    for (const eventName of events) {
      const diff = Time.now() - (hub.data.extensionEvents[eventName] ?? 0)

      if (diff > 10 * Time.minute) {
        delete hub.data.extensionEvents[eventName]
      }
    }
  }

  static setExtensionEventTimestamp(
    hub: Hub | HubJSON,
    eventName: string
  ): void {
    hub.data ??= {}
    hub.data.extensionEvents ??= {}
    hub.data.extensionEvents[eventName] = Time.now()
  }

  static getExtensionCommentsHubTitle(extensionId: string): string {
    return `${extensionPublicHubTitlePrefix}_${extensionId}`
  }
}
